<template>
  <info-panel
    v-if="target"
    :item="target"
    :title="target?.title"
    :sections="sections"
    :extra-settings="extraSettings"
    @update:sections="updateSections($event)"
    @update:extra-settings="updateExtraSettings($event)"
    @close="emit('close')"
  >
    <!-- SETTINGS -->
    <template #label-showEmptySections>{{
      $t("labels.showEmptySections")
    }}</template>
    <template #label-statusInHeader>
      {{ $t("labels.statusInHeader") }}
    </template>

    <template #header v-if="settingsValues.statusInHeader && sceneNode && (sceneNode.data.valueVisible || sceneNode.data.barVisible)">
      <div>
        <h2 class="info-panel__type">{{ $t("words.currentState") }}</h2>
        <net-item-value-bar
          v-if="sceneNode.data.barVisible"
          :value="calculatedValue?.value ?? target.value"
          :unit="calculatedValue?.unit || '%'"
          :min-value="0"
          :max-value="100"
          :value-visible="sceneNode.data.valueVisible"
          class="target-infopanel__value"
        />
        <net-item-value-plain
          v-else-if="sceneNode.data.valueVisible"
          :value="calculatedValue?.value ?? target.value"
          :unit="calculatedValue?.unit ?? ''"
          class="target-infopanel__value"
        />
      </div>
    </template>

    <template #label-image>
      {{ $t("words.image") }}
    </template>
    <template
      #image
      v-if="
        imageData &&
        (extraSettings.image?.value == 'side-full' ||
          extraSettings.image?.value == 'side-cut')
      "
    >
      <img
        :src="imageData"
        :class="{
          'info-panel__image-full': extraSettings.image?.value == 'side-full',
          'info-panel__image-cut': extraSettings.image?.value == 'side-cut'
        }"
      />
    </template>
    <template
      #header-image
      v-if="imageData && extraSettings.image?.value == 'icon'"
    >
      <img :src="imageData" />
    </template>
    <template #option-image-hidden>{{ $t("labels.hidden") }}</template>
    <template #option-image-icon>{{ $t("labels.imageAsIcon") }}</template>
    <template #option-image-side-full>{{
      $t("labels.imageAtSideFull")
    }}</template>
    <template #option-image-side-cut>{{
      $t("labels.imageAtSideCut")
    }}</template>

    <template #label-description>
      {{ $t("labels.description") }}
    </template>
    <template
      #description
      v-if="settingsValues.showEmptySections || !isEmpty(target.description)"
    >
      <formatted-read-more-text
        :value="target.description"
        :placeholder="$t('errors.noDescription')"
      />
    </template>

    <template #label-indicators>{{ $t("objectNames.indicator", 4) }}</template>
    <template #indicators>
      <div class="column">
        <div v-for="indicator of indicators" :key="indicator.id" class="target-infopanel__indicator">
          <h3 class="mv-0">{{ indicator.title }}</h3>
          <formatted-read-more-text
            :value="indicator.docs.description"
            :max-height="100"
            class="mt-1 mb-1" 
          />
        </div>
      </div>
    </template>

    <template #label-state>
      {{ $t("labels.status") }}
    </template>
    <template #state>
      <div class="info-panel__table">
        <span class="label">{{ $t("words.currentState") }}:</span>
        <span
          >{{ formatNumber(calculatedValue?.value) }}
          {{ calculatedValue?.unit }}</span
        >

        <span class="label">{{ $t("words.initialState") }}:</span>
        <span
          >{{ formatNumber(startValue?.value) }}
          {{ startValue?.unit }}</span
        >
      </div>
    </template>

    <template #label-timechart>
      {{ $t("labels.stateOverTime") }}
    </template>
    <template #timechart="{ width }">
      <Chart
        v-if="isSimulationOpen()"
        :width="width"
        :height="300"
        :chart-item="chartOptions"
      />
      <p v-else class="message mv-0 message-info">
        {{ $t("messages.runSimulationForChart") }}
      </p>
    </template>

    <template #label-impacts>
      {{ $t("objectNames.impact", 5) }}
    </template>
    <template
      #impacts
      v-if="settingsValues.showEmptySections || relations.length > 0"
    >
      <div class="line--half mt-2 gap-4">
        <div>
          <span v-if="incomingActiveRelations.length > 0" class="label">
            {{ $t("labels.incomingRelations") }}
          </span>
          <button-list
            :items="incomingActiveRelations"
            :transition-active="false"
            @select="openOverlay($event)"
          >
            <template #default="{ item: relation }: { item: RelationItem }">
              <span v-if="items[relation.startItemType][relation.startItemId]?.title">
                {{ items[relation.startItemType][relation.startItemId]?.title }}
              </span>
              <span v-else class="placeholder-text">
                {{ $t("labels.unnamed") }}
              </span>
            </template>
            <template #placeholder>
              <p class="message mv-0">
                {{ $t("errors.noIncomingRelations") }}
              </p>
            </template>
          </button-list>
          <!--
          <details v-if="incomingNoneRelations.length > 0" class="mt-2" style="font-size: 0.8em; color: var(--color-text-medium)">
            <summary>{{ $t("labels.noneRelations") }}</summary>
            <button-list
              :items="incomingNoneRelations"
              class="mt-2"
            >
              <template #placeholder>
                {{ $t("errors.noIncomingRelations") }}
              </template>
              <template #default="{ item: relation }: { item: RelationItem }">
                <span>{{
                  items[relation.startItemType][relation.startItemId].title
                }}</span>
              </template>
            </button-list>
          </details>
          -->
        </div>
        <div>
          <span class="label" v-if="outgoingActiveRelations.length > 0">{{
            $t("labels.outgoingRelations")
          }}</span>
          <button-list
            :items="outgoingActiveRelations"
            :transition-active="false"
            @select="openOverlay($event)"
          >
            <template #default="{ item: relation }: { item: RelationItem }">
              <span v-if="items[relation.endItemType][relation.endItemId]?.title">
                {{ items[relation.endItemType][relation.endItemId].title }}
              </span>
              <span v-else class="placeholder-text">
                {{ $t("labels.unnamed") }}
              </span>
            </template>
            <template #placeholder>
              <p class="message mv-0">
                {{ $t("errors.noOutgoingRelations") }}
              </p>
            </template>
          </button-list>
          <!--
          <details v-if="outgoingNoneRelations.length > 0" class="mt-2 mb-4" style="font-size: 0.8em; color: var(--color-text-medium)">
            <summary>{{ $t("labels.noneRelations") }}</summary>
            <button-list
              :items="outgoingNoneRelations"
              class="mt-2"
            >
              <template #placeholder>
                {{ $t("errors.noOutgoingRelations") }}
              </template>
              <template #default="{ item: relation }: { item: RelationItem }">
                {{ items[relation.endItemType]?.[relation.endItemId]?.title }}
              </template>
            </button-list>
          </details>
          -->
        </div>
      </div>
    </template>

    <template #label-flows>
      {{ $t("objectNames.flow", 5) }}
    </template>
    <template
      #flows
      v-if="settingsValues.showEmptySections || flowBranches.length > 0"
    >
      <div class="line--half gap-4">
        <div>
          <span class="label" v-if="incomingFlows.length > 0">
            {{ $t("labels.incomingFlows") }}
          </span>
          <button-list
            :items="incomingFlows"
            :transition-active="false"
            @select="openOverlay($event)"
          >
            <template #default="{ item: branch }: { item: FlowBranchItem }">
              <span>{{ flowStore.titles[branch.flowId] }}</span>
            </template>
            <template #placeholder>
              <p class="message mv-0">
                {{ $t("errors.noIncomingFlows") }}
              </p>
            </template>
          </button-list>
        </div>

        <div>
          <span class="label mt-2" v-if="outgoingFlows.length > 0">
            {{ $t("labels.outgoingFlows") }}
          </span>
          <button-list
            :items="outgoingFlows"
            :transition-active="false"
            @select="openOverlay($event)"
          >
            <template #default="{ item: branch }: { item: FlowBranchItem }">
              <span>{{ flowStore.titles[branch.flowId] }}</span>
            </template>
            <template #placeholder>
              <p class="message mv-0">
                {{ $t("errors.noOutgoingFlows") }}
              </p>
            </template>
          </button-list>
        </div>
      </div>
    </template>

    <template #label-actions>
      {{ $t("objectNames.action", 5) }}
    </template>
    <template #heading-actions>{{
      $t("labels.influencingActions")
    }}</template>
    <template
      #actions
      v-if="settingsValues.showEmptySections || actions.length > 0"
    >
      <div v-if="actions.length > 0" class="line_effect">
        <template v-for="action in actions" :key="action.id">
          <button-item @click="openOverlay(action)">
            <span v-if="action.title">
              {{ action.title }}
            </span>
            <span v-else class="placeholder-text">
              {{ $t("labels.unnamed") }}
            </span>
          </button-item>
          <effect-amount
            v-if="actionEffects[action.id]"
            :effect="actionEffects[action.id]!"
            :inline-expression="true"
          />
        </template>
      </div>
      <p v-else class="message mv-0">
        {{ $t("errors.noInfluencingActions") }}
      </p>
    </template>

    <template #label-incidents>
      {{ $t("objectNames.incident", 5) }}
    </template>
    <template #heading-incidents>{{
      $t("labels.influencingIncidents")
    }}</template>
    <template
      #incidents
      v-if="settingsValues.showEmptySections || incidents.length > 0"
    >
      <div v-if="incidents.length > 0" class="line_effect">
        <template v-for="incident in incidents" :key="incident.id">
          <button-item @click="openOverlay(incident)">
            <span v-if="incident.title">
              {{ incident.title }}
            </span>
            <span v-else class="placeholder-text">
              {{ $t("labels.unnamed") }}
            </span>
          </button-item>
          <effect-amount
            :effect="incidentEffects[incident.id]"
            :inline-expression="true"
          />
        </template>
      </div>
      <p v-else class="message mv-0">
        {{ $t("errors.noInfluencingIncidents") }}
      </p>
    </template>

    <template #label-scenarios>
      {{ $t("objectNames.scenario", 5) }}
    </template>
    <template #scenarios v-if="changingScenarios.length > 0">
      <button-list
        :items="changingScenarios"
        line-class="line_effect"
        :transition-active="false"
        @select="openOverlay($event)"
      >
        <template #default="{ item }">
          <span v-if="item?.title">{{ item.title }}</span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>
        </template>
        <template #after="{ item }">
          <span class="ta-r">{{ item.targetValues[target.id] }}</span>
        </template>
        <template #placeholder>
          <p>
            {{ $t("errors.nostartValuesInScenarios") }}
          </p>
        </template>
      </button-list>
    </template>

    <template #label-stakeholders>
      {{ $t("objectNames.stakeholder", 2) }}
    </template>
    <template
      #stakeholders
      v-if="settingsValues.showEmptySections || stakeholders.length > 0"
    >
      <div class="line--wrap" v-if="stakeholders.length > 0">
        <button-item
          v-for="stakeholder in stakeholders"
          :key="'stakeholder' + stakeholder.id"
          @click="openOverlay(stakeholder)"
        >
          <span v-if="stakeholder.title">
            {{ stakeholder.title }}
          </span>
          <span v-else class="placeholder-text">
            {{ $t("labels.unnamed") }}
          </span>
        </button-item>
      </div>
      <p v-else class="message mv-0">
        {{ $t("errors.noStakeholdersAssigned") }}
      </p>
    </template>
  </info-panel>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { useRoute } from "vue-router";
import { omitBy, mapValues, find, isNil, map } from "lodash-es";
import { ensureInt, notNull } from "@/helpers/utility/Type";
import { pickBy } from "@/helpers/utility/CollectionFunctions";
import { isEmpty } from "@/helpers/utility/Text";
import { formatNumber } from "@/helpers/utility/Formatting";

import ButtonList from "@core/ui/ButtonList.vue";
import InfoPanel from "@editor/ui/InfoPanel.vue";

import EffectAmount from "@editor/items/effect/EffectAmount.vue";
import FormattedReadMoreText from "@core/ui/FormattedReadMoreText.vue";
import ButtonItem from "@core/input/ButtonItem.vue";
import NetItemValueBar from "@editor/net/NetItemValueBar.vue";
import NetItemValuePlain from "@editor/net/NetItemValuePlain.vue";
import Chart from "../charts/Chart.vue";

import { useFlowStore } from "../flow/flows.store";
import { useFlowBranchStore } from "../flow/flowBranches.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useActionStore } from "@editor/items/action/actions.store";
import { useEffectStore } from "@editor/items/effect/effects.store";
import { useIncidentStore } from "@editor/items/incident/incidents.store";
import { useScenarioStore } from "@editor/items/scenario/scenarios.store";
import { useStakeholderStore } from "@editor/items/stakeholder/stakeholders.store";
import { useRelationStore } from "@editor/items/relation/relations.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useIndexStore } from "../index/indices.store";
import { useImageStore } from "../image/images.store";
import { useInfoPanelStore } from "@editor/store/infoPanels.store";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";
import { useInspectorView } from "@editor/views/inspector/inspectorView.store";
import { useIndicatorStore } from "../indicator/indicators.store";
import { useSettingsStore } from "@editor/store/settings.store";

import type { InfoPanelSetting } from "@editor/store/infoPanels.store";
import type { RelationItem } from "../relation/RelationItem";
import type { EffectItem } from "../effect/EffectItem";
import type { FlowBranchItem } from "../flow/FlowBranchItem";
import type { TargetItem } from "./TargetItem";
import type { OverlayDialogItemRequest } from "@editor/ui/OverlayDialogItem";
import type { ChartItemOptions } from "../charts/useChartData";
import { useSceneTree } from "../scene/sceneTree";

const props = defineProps<{
  target: TargetItem | null;
}>();
const emit = defineEmits<{
  (e: "close"): void;
  (e: "select", item: OverlayDialogItemRequest): void;
}>();

const imageStore = useImageStore();
const flowStore = useFlowStore();
const infoPanelStore = useInfoPanelStore();
const indicatorStore = useIndicatorStore();
const simulationViewStore = useSimulationViewStore();
const route = useRoute();

const sections = reactive({
  description: true,
  indicators: true,
  timechart: true,
  state: true,
  impacts: true,
  flows: true,
  actions: true,
  incidents: true,
  scenarios: true,
  stakeholders: true
});

const extraSettings = reactive({
  image: {
    type: "dropdown" as const,
    value: "icon",
    options: ["icon", "side-full", "side-cut", "hidden"]
  },
  showEmptySections: { type: "check" as const, value: true },
  statusInHeader: { type: "check" as const, value: true }
});

const targets = computed(() => {
  return useTargetStore().items;
});
const resources = computed(() => {
  return useResourceStore().items;
});
const indices = computed(() => {
  return useIndexStore().items;
});
const items = computed(() => {
  return {
    target: targets.value,
    resource: resources.value,
    index: indices.value
  };
});

// ================
// VALUE

const startValue = computed(() => {
  if (props.target == null) return null;
  return useTargetStore().expressionValues[props.target.uid] ?? null;
});

const calculatedValue = computed(() => {
  if (props.target == null) return null;
  if (!isSimulationOpen()) return startValue.value;
  return simulationViewStore.elementValues[props.target.uid];
});

function isSimulationOpen() {
  if (!["simulation", "inspector"].includes(route.meta.areaName as string))
    return false;
  if (route.meta.areaName === "inspector") {
    if (useInspectorView().subview != "matrix") return false;
  }
  return simulationViewStore.simulation != null;
}

// ================
// NODE

const sceneTree = useSceneTree();

const treeNode = computed(() => {
  if (props.target == null) return null;
  return sceneTree.elementNodes.get(props.target.uid) || null;
});
const sceneNode = computed(() => treeNode.value?.node ?? null);

// ================
// Chart

const maxRoundRange = computed(() => {
  return Math.max(useSettingsStore().simulationRoundAmount - 1, (simulationViewStore.simulation?.maxRounds ?? 0) - 1, 0);
});

const chartOptions = computed<ChartItemOptions>(() => ({
  uid: props.target?.uid + "-infochart",
  chartType: "line",
  data: {
    target: [props.target?.id ?? -1],
    simulation: [-1],
    simulationRound: [0, maxRoundRange.value]
  },
  chartSettings: {
    scaleColorsVisible: false,
    roundEffectsVisible: false,
    titleVisible: false,
    groupLabelsVisible: false
  },
  itemConfigs: {
    target: {
      [props.target?.id ?? -1]: {
        color: "var(--color-text)",
        open: false
      }
    }
  }
}));

// ================
// Indicators

const indicators = computed(() => {
  if (props.target == null) return [];
  return indicatorStore.byTarget[props.target.id] ?? [];
});


// ================
// Relations

const incomingRelations = computed((): RelationItem[] => {
  if (props.target == null) return [];
  return useRelationStore().byEndUid[props.target.uid] ?? [];
});
const outgoingRelations = computed((): RelationItem[] => {
  if (props.target == null) return [];
  return useRelationStore().byStartUid[props.target.uid] ?? [];
});
const relations = computed((): RelationItem[] => {
  return incomingRelations.value.concat(outgoingRelations.value);
});

const incomingActiveRelations = computed((): RelationItem[] => {
  return incomingRelations.value.filter(
    (relation) => relation.updateType != "none"
  );
});
const outgoingActiveRelations = computed((): RelationItem[] => {
  return outgoingRelations.value.filter(
    (relation) => relation.updateType != "none"
  );
});

// const incomingNoneRelations = computed((): RelationItem[] => {
//   return incomingRelations.value.filter((relation) => relation.updateType == "none");
// });
// const outgoingNoneRelations = computed((): RelationItem[] => {
//   return outgoingRelations.value.filter((relation) => relation.updateType == "none");
// });

// Flows

const flowBranches = computed(() => {
  if (props.target == null) return [];
  return useFlowBranchStore().byElementUid[props.target.uid] ?? [];
});
const incomingFlows = computed(() => {
  return flowBranches.value.filter(
    (branch) => branch.branchType == "sink"
  );
});
const outgoingFlows = computed(() => {
  return flowBranches.value.filter(
    (branch) => branch.branchType == "source"
  );
});

const actions = computed(() => {
  if (props.target == null) return [];
  const actions = useActionStore().byTarget[props.target.id];
  if (actions == null) return [];
  return Object.values(actions);
});
const actionEffects = computed(() => {
  const effectStore = useEffectStore();
  return omitBy(
    mapValues(effectStore.byAction, (effects) =>
      find(
        effects,
        (effect) =>
          effect.affectedType === "target" &&
          effect.affectedId === props.target?.id
      )
    ),
    isNil
  );
});

const incidentEffects = computed(() => {
  const effectStore = useEffectStore();
  return pickBy(
    mapValues(effectStore.byIncident, (effects) =>
      find(
        effects,
        (effect) =>
          effect.affectedType === "target" &&
          effect.affectedId === props.target?.id
      )
    ),
    notNull
  ) as { [key: number]: EffectItem };
});

const incidents = computed(() => {
  const incidentStore = useIncidentStore();
  const incidentIds = map(incidentEffects.value, (effect) => {
    if (effect.activatorId == null) return null;
    return ensureInt(effect.activatorId);
  }).filter(notNull);
  return incidentStore.getArrayByIds(incidentIds).sort(incidentStore.sortFunction);
});

const changingScenarios = computed(() => {
  const scenarioStore = useScenarioStore();
  return scenarioStore.orderedItems.filter((scenario) =>
    Object.keys(scenario.targetValues ?? {}).includes(props.target?.id + "")
  );
});
const stakeholders = computed(() => {
  if (props.target == null) return [];
  return useStakeholderStore().byTarget[props.target.id] ?? [];
});

const image = computed(() => {
  if (sceneNode.value && sceneNode.value.data.imageId != null) {
    return imageStore.items[sceneNode.value.data.imageId];
  }
  return null;
});

const imageData = computed(() => {
  if (!image.value) return undefined;
  return imageStore.mediaData[image.value.id] ?? undefined;
});

// ================
// FUNCTIONS

function openOverlay(item: OverlayDialogItemRequest) {
  emit("select", item);
}

function updateSections(value: { [section: string]: boolean }) {
  Object.assign(sections, value);
  infoPanelStore.sections.target = sections;
}

function loadSections() {
  const savedSections = infoPanelStore.sections?.target || {};
  for (const key in sections) {
    if (key in savedSections) {
      sections[key as keyof typeof sections] = savedSections[key];
    }
  }
}

function updateExtraSettings(value: { [setting: string]: InfoPanelSetting }) {
  Object.assign(extraSettings, value);
  infoPanelStore.extraSettings.target = extraSettings;
}

function loadExtraSettings() {
  const savedExtraSettings = infoPanelStore.extraSettings?.target || {};
  for (const key in extraSettings) {
    if (key in savedExtraSettings) {
      extraSettings[key as keyof typeof extraSettings] = {
        ...(savedExtraSettings[key] as any)
      };
    }
  }
}

const settingsValues = computed(() => {
  return mapValues(extraSettings, (setting) => setting.value);
});

loadExtraSettings();
loadSections();
</script>

<style lang="scss">
.target-infopanel {
  &__value {
    font-size: 14px;
    width: 11rem;
    height: 1.25rem;
    flex-shrink: 0;
    align-self: flex-end;
    justify-content: flex-start;
    margin-top: 0.45rem;

    .element-valuebar__valueblock {
      font-size: 11px;
      margin-top: -1px;
    }
  }

  &__indicator {
    list-style-type: disc;
    h3 {
      position: relative;
      padding-left: 1rem;
      font-size: 1rem;
      color: var(--color-text-medium);
      &::before {
        content: "•";
        position: absolute;
        left: 0;
      }
    }
    & > div {
      padding-left: 1rem;
    }
  }
}
</style>
